import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useActivityToolWithProgress } from '@/data/hook/editorjs/useActivityToolProgress';
import useLessonContext from '@/data/hook/lesson';
import { Section } from '@/models/Book';
import { UserTypeEnum } from '@/models/User';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { QuizRaw } from '@/utils/editorjs/activity-block/model/quiz';
import { mountKey } from '@/utils/material/chapter/chapterLayout';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import IconButton from '../../../../buttons/IconButton';
import MaterialEditorJS from '../MaterialEditorJS';
import SectionContainer from './SectionContainer';
import SectionTitle from './SectionTitle';

export type SectionViewProps = {
  section: Section;
  className?: ClassNameComponent;
};

export default function SectionView({ section, className }: SectionViewProps) {
  const StartsHiddenIcon = section.startsHidden ? EyeSlashIcon : EyeIcon;
  const { lessonProgress, updateLessonProgress } = useLessonContext();

  const getActivityProgress = async (quiz: QuizRaw) => {
    const activityProgress = lessonProgress?.activitiesProgress?.find(
      activityProgress => activityProgress.activity.id === quiz.activityId,
    );
    return activityProgress;
  };

  const { tool } = useActivityToolWithProgress(
    getActivityProgress,
    updateLessonProgress,
  );

  return (
    <SectionContainer className={className?.base}>
      <main className={className?.main} data-testid={mountKey(section)}>
        <header
          className={twMerge(
            'flex items-center gap-2.5 flex-wrap',
            className?.section,
          )}
        >
          <SectionTitle title={section.title} icon={section.icon} />
          {/* TODO: enable when has web socket back impl */}
          <ConditionalRenderer condition={false}>
            <ComponentGuard roles={[UserTypeEnum.TEACHER]}>
              <IconButton
                icon={<StartsHiddenIcon className="w-4 h-4 text-secondary" />}
                type="button"
              />
            </ComponentGuard>
          </ConditionalRenderer>
        </header>

        <MaterialEditorJS
          readOnly
          value={section.content}
          minHeight={0}
          activityBlock={tool}
        />
      </main>
    </SectionContainer>
  );
}
